/**
 * Result Enter Popup – Histopathology result entry dialog.
 * Exact match of renderResultUploadPopup() in admin4.0 HistopathologyReport.js.
 */
import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  TextField,
} from "@mui/material";
import NewToolsFieldWithMic from "../../Components/Common Components/NewToolsFieldWithMic";
import { ImagePaths } from "../../Utility/ImagePaths";

const closeImg = ImagePaths?.LabCloseButton?.default ?? ImagePaths?.LabCloseButton;

export default function ResultEnterPopup({
  open,
  onClose,
  signatureData = [],
  templateData = [],
  popupData = null,
  popupLoading = false,
  errorMessage,
  onSave,
  selectedServiceData,
  resultTextData: resultTextDataProp,
  editorDisable: editorDisableProp = false,
  isDispatchshown = true,
  postButtonLabel = "Send Approval",
  showOnlyApproveButton = false,
  hideSaveAndPostButtons = false,
  viewOnly = false,
  uploadedAttachment = [],
  resultPDF = [],
}) {
  const [selectedLabIncharge, setSelectedLabIncharge] = useState(null);
  const [selectedPathologist2, setSelectedPathologist2] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [resultTextData, setResultTextData] = useState("");

  const leftSignOptions = popupData?.lab_incharge_options?.length ? popupData.lab_incharge_options : (signatureData || []);
  const rightSignOptions = popupData?.pathologist_options?.length ? popupData.pathologist_options : (signatureData || []);

  const getSignOptionLabel = (option) => {
    if (!option || typeof option === "string") return option ?? "";
    if (option.login_fname != null || option.login_lname != null) {
      return [option.login_fname, option.login_lname].filter(Boolean).join(" ").trim() || option.login_name || "";
    }
    return `${option?.role_name ?? ""} - ${option?.name ?? ""}`.trim() || "";
  };

  const isCompletedOrDispatched =
    selectedServiceData?.result_status === "Completed" ||
    selectedServiceData?.result_status === "Dispatched";

  const disableDropdowns = editorDisableProp || isCompletedOrDispatched || viewOnly;

  useEffect(() => {
    if (!open) {
      setSelectedLabIncharge(null);
      setSelectedPathologist2(null);
      setSelectedTemplate(null);
      setResultTextData("");
      return;
    }
    if (popupData) {
      setResultTextData(popupData.report_content ?? "");
      const leftOpts = popupData.lab_incharge_options ?? [];
      const rightOpts = popupData.pathologist_options ?? [];
      const templates = Array.isArray(templateData) ? templateData : [];
      setSelectedLabIncharge(popupData.lab_incharge_id != null ? (leftOpts.find((o) => o.id === popupData.lab_incharge_id) ?? null) : null);
      setSelectedPathologist2(popupData.pathologist_2_id != null ? (rightOpts.find((o) => o.id === popupData.pathologist_2_id) ?? null) : null);
      setSelectedTemplate(popupData.result_template_id != null ? (templates.find((t) => t.id === popupData.result_template_id) ?? null) : null);
      return;
    }
    if (selectedServiceData) {
      setResultTextData(selectedServiceData?.result_data ?? resultTextDataProp ?? "");
      if (selectedServiceData?.esign_left_id && signatureData?.length) {
        const left = signatureData.find((s) => s.id === selectedServiceData.esign_left_id);
        setSelectedLabIncharge(left ?? null);
      } else setSelectedLabIncharge(null);
      if (selectedServiceData?.esign_right_id && signatureData?.length) {
        const right = signatureData.find((s) => s.id === selectedServiceData.esign_right_id);
        setSelectedPathologist2(right ?? null);
      } else setSelectedPathologist2(null);
      if (selectedServiceData?.template_name && Array.isArray(templateData) && templateData.length > 0) {
        const t = templateData.find((tpl) => tpl?.template_name === selectedServiceData?.template_name);
        setSelectedTemplate(t ?? null);
        if (t?.template_data) setResultTextData(t.template_data);
      } else setSelectedTemplate(null);
    }
  }, [open, selectedServiceData?.id, signatureData, templateData, popupData?.result_detail_id]);

  useEffect(() => {
    if (typeof resultTextDataProp === "string" && open && selectedServiceData && !selectedTemplate && !popupData) {
      setResultTextData(resultTextDataProp);
    }
  }, [open, resultTextDataProp, selectedServiceData?.id, popupData]);

  const handleClose = () => {
    setSelectedLabIncharge(null);
    setSelectedPathologist2(null);
    setSelectedTemplate(null);
    setResultTextData("");
    onClose?.();
  };

  const handleTemplateChange = (e, value) => {
    setSelectedTemplate(value);
    setResultTextData(value?.template_data ?? "");
  };

  const handleSaveResult = (resultHtml, isForSave, isForCompleted, pdfUploadList, capturedImg) => {
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isForSave, isForCompleted, pdfUploadList, capturedImg, selectedTemplate });
  };

  const handlePostResult = (resultHtml, isForSave, isForCompleted, pdfUploadList, capturedImg) => {
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isForSave, isForCompleted, pdfUploadList, capturedImg, selectedTemplate });
    handleClose();
  };

  const handleDispatchResult = (resultHtml) => {
    onSave?.(resultHtml, selectedLabIncharge, selectedPathologist2, { isDispatch: true });
    handleClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose} PaperProps={{ sx: { overflow: "hidden" } }}>
      {popupLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
      <Box
        component="div"
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "85vh",
          minHeight: 400,
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "nowrap",
            flexShrink: 0,
            padding: "0.5vw",
            marginTop: "0.5vw",
            marginRight: "0.5vw",
            gap: 0,
          }}
        >
          <Autocomplete
            value={selectedLabIncharge || null}
            options={leftSignOptions}
            disabled={disableDropdowns}
            getOptionLabel={getSignOptionLabel}
            size="small"
            onChange={(e, value) => setSelectedLabIncharge(value)}
            sx={{ width: "15vw", minWidth: 160, marginRight: "1vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Left E-Sign" />
            )}
          />
          <Autocomplete
            value={selectedPathologist2 || null}
            options={rightSignOptions}
            disabled={disableDropdowns}
            getOptionLabel={getSignOptionLabel}
            size="small"
            onChange={(e, value) => setSelectedPathologist2(value)}
            sx={{ width: "15vw", minWidth: 160, marginRight: "2vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Right E-Sign" />
            )}
          />
          <Autocomplete
            value={selectedTemplate || null}
            options={Array.isArray(templateData) ? templateData : []}
            disabled={disableDropdowns}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.template_name ?? ""
            }
            size="small"
            onChange={handleTemplateChange}
            sx={{ width: "20vw", minWidth: 180, marginRight: "2vw" }}
            renderInput={(params) => (
              <TextField {...params} label="Select Template" />
            )}
          />
          <Button
            className="Common_Btn_Min_Width"
            onClick={handleClose}
            sx={{ minWidth: 40, p: 0.5, flexShrink: 0 }}
          >
            <Box
              component="img"
              src={
                typeof closeImg === "object" && closeImg?.default
                  ? closeImg.default
                  : closeImg
              }
              height="1vw"
              width="1vw"
              alt="Close"
              sx={{ minHeight: 20, minWidth: 20, display: "block" }}
            />
          </Button>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <NewToolsFieldWithMic
          FromTestReport={true}
          selectedServiceData={selectedServiceData}
          EditorDisable={editorDisableProp || viewOnly}
          isDispatchshown={isDispatchshown}
          Title="Result"
          EditorRef={resultTextData}
          handleSaveResult={handleSaveResult}
          handlePostResult={handlePostResult}
          handleDispatchResult={handleDispatchResult}
          overallHeight="100%"
          EditorHeight="53vh"
          ResultPDF={[...(uploadedAttachment || []), ...(resultPDF || [])]}
          postButtonLabel={postButtonLabel}
          showOnlyApproveButton={showOnlyApproveButton}
          hideSaveAndPostButtons={hideSaveAndPostButtons}
          viewOnly={viewOnly}
          micTextEditDisabled={true}
          addSpaceOnTab={true}
          errorMsg={errorMessage}
        />
        </Box>
      </Box>
      )}
    </Dialog>
  );
}
